import { forwardRef, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import Card from "../../common/components/Card";
import DataTable from "../../common/components/DataTable";
import DefaultButton from "../../common/components/DefaultButton";
import { Row, Col } from "../../common/components/Grid";
import Icon from "../../common/components/Icon";
import ReactForm from "../../common/components/ReactForm";
import TextInput from "../../common/components/TextInput";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { deepClone, deepEquals } from "../../common/utils/objectUtilities";
import { dynamicSort } from "../../common/utils/tableUtilities";
import { canInsertProcedure, canRemoveProcedure, canUpdateProcedure, deleteProcedureStep, insertProcedure, insertProcedureStep, removeProcedure, updateProcedure, updateProcedureStep, useAllProcedures, useProcedureSteps } from "../../reducers/procedureSlice";
import SimpleSettings from "./SimpleSettings";
import PropTypes from "prop-types";
import { DragTarget, DropTarget } from "../../common/components/DragDropComponents";

const newProcedureTemplate = { applications: [], steps: [] };
const newStepTemplate = {};
const defaultSort = ['title'];

const SOPLabel = ({ value: procedure }) => {
    return <Row>
        <Col className="text-left">
            {procedure.title}
        </Col>
    </Row>
}

const SOPDetails = forwardRef(({ value: procedure, disabled, isNew, onChange }, ref) => {
    const { t } = useTranslation();
    const network = useNetwork();
    const [newStep, setNewStep] = useState(deepClone(newStepTemplate));
    const procedureSteps = useProcedureSteps(procedure.id);
    const steps = isNew ? procedure.steps : procedureSteps;
    const displaySteps = useMemo(() => {
        const displaySteps = [...steps];
        displaySteps.sort(dynamicSort(['index']));
        return displaySteps;
    }, [steps])

    const handleStepUpdate = (updates) => {
        if (isNew) {
            onChange({ steps: steps.map(s => updates.filter(({ step }) => s === step).reduce((s, { field, value }) => { s[field] = value; return s; }, { ...s })) });
        } else {
            updates.forEach(({ step, field, value }) => {
                updateProcedureStep(network, { stepid: step.stepid, [field]: value });
            })
        }
    }

    const moveStepUp = (index) => {
        const index1 = displaySteps[index].index;
        const index2 = displaySteps[index - 1].index
        handleStepUpdate([{ step: displaySteps[index], field: 'index', value: index2 }, { step: displaySteps[index - 1], field: 'index', value: index1 }]);
    }

    const moveStepDown = (index) => {
        const index1 = displaySteps[index].index;
        const index2 = displaySteps[index + 1].index
        handleStepUpdate([{ step: displaySteps[index], field: 'index', value: index2 }, { step: displaySteps[index + 1], field: 'index', value: index1 }]);
    }

    const moveStep = (sourceIndex, destIndex) => {
        const start = Math.min(sourceIndex, destIndex);
        const end = Math.max(sourceIndex, destIndex);
        const indices = displaySteps.slice(start, end + 1).map(step => step.index);
        if (sourceIndex < destIndex) {
            indices.unshift(indices.pop())
        } else {
            indices.push(indices.shift());
        }
        handleStepUpdate(displaySteps.slice(start, end + 1).map((step, index) => ({ step, field: 'index', value: indices[index]})));
    }

    const removeStep = (index) => {
        if (isNew) {
            onChange({ steps: displaySteps.slice(index + 1).map(s => ({ ...s, index: s.index - 1 })) });
        } else {
            deleteProcedureStep(network, displaySteps[index]);
            displaySteps.slice(index + 1).forEach(step => updateProcedureStep(network, { stepid: step.stepid, index: step.index - 1 }))
        }
    }

    const addStep = () => {
        let index = steps.reduce((index, step) => index === null || index < step.index ? step.index : index, null);
        if (index === null) {
            index = 0;
        } else {
            index++;
        }
        if (isNew) {
            onChange({ steps: [ ...procedure.steps, { ...newStep, index }]});
        } else {
            insertProcedureStep(network, { ...newStep, index, procedureid: procedure.id })
        }
        setNewStep(deepClone(newStepTemplate));
    }

    const columns = useMemo(() => [
        { type: 'site', id: 'site', field: ['site','level','type'], label: t('site', { count: 1 }), title: t('site', { count: 1 }), sortfield: 'site', strict: false, wildcard: t('anyWildcard') },
        { type: 'level', id: 'level', field: ['site','level','type'], label: t('level', { count: 1 }), title: t('level', { count: 1 }), sortfield: 'level', strict: false, wildcard: t('anyWildcard') },
        { type: 'type', id: 'type', field: ['site','level','type'], label: t('type', { count: 1 }), title: t('type', { count: 1 }), sortfield: 'type', strict: false, wildcard: t('anyWildcard') },
        { type: 'problem', id: 'problem', field: ['site','level','type','problem'], label: t('problem', { count: 1 }), title: t('problem', { count: 1 }), sortfield: 'problem', strict: false },
        { type: 'priority', id: 'priority', field: 'priority', label: t('prority', { count: 1 }), title: t('priority', { count: 1 }), sortfield: 'priority', strict: false, wildcard: t('anyWildcard') }
    ], [t]);

    return <>
        <ReactForm ref={ref}>
            <Row>
                <Col>
                    <TextInput value={procedure.title} maxLength={256} placeholder={t('title')} disabled={disabled} onChange={title => onChange({ title })} required></TextInput>
                </Col>
            </Row>
        </ReactForm>
        <Card title={t('applications')}>
            <DataTable columns={columns} value={procedure.applications ?? []} disabled={disabled} defaultSort={['site','level','type','problem','priority']} 
            canAdd={entry => procedure.applications.every(a => !deepEquals(a, entry))}
            onAdd={application => onChange({ applications: [ ...procedure.applications, application ] })} 
            onRemove={application => onChange({ applications: procedure.applications.filter(a => !deepEquals(a, application)) })}></DataTable>
        </Card>
        <Card title={t('step', { count: steps?.length ?? 0 })}>
            <div className="d-flex flex-column">
                {displaySteps.map((step, idx) => <DropTarget kind="SOP" version={1} onDrop={data => moveStep(data.sourceIndex, idx)}>
                    <Row key={step.id} className="mb-2">
                        <Col md>
                            <Row>
                                { !disabled && <DragTarget kind="SOP" version={1} data={{ sourceIndex: idx }}><Col className="mx-2" xs="auto" style={{ cursor: 'grab' }}>
                                    <Icon variant="solid" kind="grip-lines-vertical"></Icon>
                                </Col></DragTarget> }
                                <Col>
                                    <TextInput value={step.text} hideLabel={true} disabled={disabled} onChange={value => handleStepUpdate([{ step,  field: 'text', value }])}></TextInput>
                                </Col>
                            </Row>
                        </Col>
                        { !disabled && <Col className="text-right" md="auto">
                            <Row>
                                <Col></Col>
                                { idx > 0 && <Col className="mx-1" xs="auto">
                                    <Button variant="outline-dark" onClick={() => moveStepUp(idx)}><Icon intent="up"></Icon></Button>
                                </Col> }
                                { idx < steps.length - 1 && <Col className="mx-1" xs="auto">
                                    <Button variant="outline-dark" onClick={() => moveStepDown(idx)}><Icon intent="down"></Icon></Button>
                                </Col> }
                                <Col className="mx-1" xs="auto">
                                    <DefaultButton kind="delete" variant="outline-danger" onClick={() => removeStep(idx)}></DefaultButton>
                                </Col>
                            </Row>
                        </Col> }
                    </Row>
                </DropTarget>)}
                { !disabled && <Row>
                    <Col md>
                        <TextInput hideLabel={true} value={newStep.text} onChange={value => setNewStep(step => ({ ...step, text: value }))}></TextInput>
                    </Col>
                    <Col className="text-right" md="auto">
                        <Row>
                            <Col></Col>
                            <Col className="mx-1" xs="auto">
                                <DefaultButton kind="add" variant="outline-primary" disabled={!newStep.text} onClick={() => addStep()}></DefaultButton>
                            </Col>
                        </Row>
                    </Col>
                </Row> }
            </div>
        </Card>
    </>
})

const SOPs = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const network = useNetwork();
    const credentials = useCredentials();
    const procedures = useAllProcedures();
    const sortFields = useMemo(() => {
        let fields = { name: t('name') }
        return fields;
    }, [t]);

    const handleAdd = async (procedure) => {
        const toAdd = { ...procedure };
        const steps = toAdd.steps;
        delete toAdd.steps;
        const result = await insertProcedure(network, toAdd)
        if (result) {
            steps.forEach(step => insertProcedureStep(network, { ...step, procedureid: result.id }));
        }
        return result;
    }

    return <SimpleSettings data={procedures} uuid={uuid} label={label} pkey="id" defaultSort={defaultSort} sortFields={sortFields}
        templateItem={newProcedureTemplate} canAdd={sop => canInsertProcedure(credentials, sop)}
        canUpdate={sop => canUpdateProcedure(credentials, sop)}
        canRemove={sop => canRemoveProcedure(credentials, sop)}
        checkValidity={sop => sop.id || sop.steps.length > 0}
        disableCopy={true}
        onAdd={handleAdd}
        onRemove={sop => removeProcedure(network, sop)}
        onChange={(sop, delta) => updateProcedure(network, { id: sop.id, ...delta })}
        onClose={onClose} ItemLabel={SOPLabel} ItemDetails={SOPDetails}></SimpleSettings>
}
SOPs.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}

export default SOPs;